/** @typedef {"eta"|"etd"|"task"} CalendarEventKind */

/**
 * @typedef {Object} CalendarEvent
 * @property {string} date ISO date YYYY-MM-DD
 * @property {CalendarEventKind} kind
 * @property {string} id
 * @property {string} label
 * @property {?string} risk
 * @property {boolean} overdue
 * @property {string} detail_url
 * @property {?string} route
 */

/**
 * @param {string} isoDate
 * @return {Date}
 */
export function parseCalendarDate(isoDate) {
  return new Date(`${isoDate}T12:00:00Z`);
}

/**
 * @param {Date} date
 * @return {string}
 */
export function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * @param {Date} start
 * @param {number} count
 * @return {string[]}
 */
export function rollingDayRange(start, count) {
  const days = [];
  for (let index = 0; index < count; index += 1) {
    const day = new Date(start);
    day.setUTCDate(day.getUTCDate() + index);
    days.push(toIsoDate(day));
  }
  return days;
}

/**
 * Four Monday–Sunday weeks covering today.
 * @param {string} isoToday
 * @return {string[]}
 */
export function fourWeekRange(isoToday) {
  const start = parseCalendarDate(isoToday);
  const mondayOffset = (start.getUTCDay() + 6) % 7;
  start.setUTCDate(start.getUTCDate() - mondayOffset);
  return rollingDayRange(start, 28);
}

/**
 * @param {number} year
 * @param {number} month 0-indexed
 * @return {string[]}
 */
export function monthDayRange(year, month) {
  const days = [];
  const cursor = new Date(Date.UTC(year, month, 1));
  while (cursor.getUTCMonth() === month) {
    days.push(toIsoDate(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

/**
 * @param {CalendarEvent[]} events
 * @return {Map<string, CalendarEvent[]>}
 */
export function groupEventsByDate(events) {
  const map = new Map();
  events.forEach(event => {
    const list = map.get(event.date) || [];
    list.push(event);
    map.set(event.date, list);
  });
  return map;
}

/**
 * @param {CalendarEvent[]} events
 * @return {CalendarEvent[]}
 */
export function overdueEvents(events) {
  return events.filter(event => event.overdue);
}

/**
 * @param {CalendarEvent[]} events
 * @param {string} isoToday
 * @param {number} [limit]
 * @return {CalendarEvent[]}
 */
export function upcomingCalendarEvents(events, isoToday, limit = 8) {
  return [...events]
    .filter(event => event.date >= isoToday)
    .sort((left, right) => left.date.localeCompare(right.date) || String(left.label).localeCompare(String(right.label)))
    .slice(0, limit);
}

/**
 * @param {CalendarEvent[]} events
 * @param {string} date
 * @return {CalendarEvent[]}
 */
export function eventsForDay(events, date) {
  return events.filter(event => event.date === date);
}

/**
 * @param {CalendarEvent} event
 * @return {string}
 */
export function calendarMarkClass(event) {
  if (event.kind === "task") return "operations-calendar-mark--task";
  if (event.overdue || event.risk === "late") return "operations-calendar-mark--late";
  if (event.risk === "at_risk") return "operations-calendar-mark--risk";
  return "operations-calendar-mark--eta";
}

/**
 * @param {CalendarEvent} event
 * @return {string}
 */
export function calendarPreviewClass(event) {
  return calendarMarkClass(event).replace("operations-calendar-mark", "operations-calendar-preview");
}

/**
 * @param {CalendarEvent} event
 * @return {string}
 */
export function calendarEventKindLabel(event) {
  if (event.kind === "task") return "Task";
  if (event.kind === "etd") return "ETD";
  return "ETA";
}
